/* eslint-disable @typescript-eslint/no-unused-vars */
import { base } from '../base';
import { Theme } from '../theme';
import { createFontFace } from '../base/font-face';
import InterRegularWoff from '../Inter/Inter-Regular.woff';
import InterRegularWoff2 from '../Inter/Inter-Regular.woff2';
import InterMediumWoff from '../Inter/Inter-Medium.woff';
import InterMediumWoff2 from '../Inter/Inter-Medium.woff2';
import InterSemiBoldWoff from '../Inter/Inter-SemiBold.woff';
import InterSemiBoldWoff2 from '../Inter/Inter-SemiBold.woff2';
import InterBoldWoff from '../Inter/Inter-Bold.woff';
import InterBoldWoff2 from '../Inter/Inter-Bold.woff2';

import { primitiveColors } from '../colors';
import { buildComponentSizes } from '../component-sizes';
import { semanticSizes } from '../semantic-sizes';
import { primitiveSizes } from '../sizes';
import { componentColors } from './component-colors';
import { semanticColors } from './semantic-colors';
import { semanticTypography } from './semantic-typography';
import { fontCode } from '../font-code';

export const fontFace = createFontFace({
    family: 'Inter',
    weights: [
        { weight: 400, woff: InterRegularWoff, woff2: InterRegularWoff2 },
        { weight: 500, woff: InterMediumWoff, woff2: InterMediumWoff2 },
        { weight: 600, woff: InterSemiBoldWoff, woff2: InterSemiBoldWoff2 },
        { weight: 700, woff: InterBoldWoff, woff2: InterBoldWoff2 },
    ],
});

const brandColor = primitiveColors.skydome[400];

const beige = '#E1E1E7';
const orange = '#FF9900';

const black = primitiveColors.gray['000'];
const white = primitiveColors.gray.fff;

const colorPrimary = brandColor;
const colorSecondary = primitiveColors.gray[300];

export const cloudcharge: Theme = {
    ...base,
    name: 'CloudCharge',

    ...semanticColors,
    ...semanticSizes,
    componentColors,
    componentSizes: buildComponentSizes(semanticTypography),
    typography: semanticTypography,
    colorPrimary,
    colorSecondary,
    modalBackgroundColor: white,
    firstSideBackgroundColor: primitiveColors.gray[200],
    secondSideBackgroundColor: white,

    errorColor: primitiveColors.red[500],
    activeColor: brandColor,
    focusColor: black,
    successColor: primitiveColors.green[500],
    disabledColor: primitiveColors.gray[700],

    // Typography
    fontBase: 'Inter, sans-serif',
    fontCode,
    textColor: black,
    headerColor: colorPrimary,
    textSize: '16px',
    lineHeight: '20px',
    descriptionSize: '14px',
    descriptionColor: primitiveColors.gray[800],
    descriptionLineHeight: '18px',
    fontFace,
    linkTextColor: brandColor,

    // Button
    buttonColorPrimary: colorPrimary,
    buttonTextColorPrimary: white,
    buttonColorPrimaryAlternative: white,
    buttonTextColorPrimaryAlternative: black,
    buttonColorSecondary: colorSecondary,
    buttonTextColorSecondary: black,
    buttonTextColorDisabled: primitiveColors.gray[500],
    buttonColorDisabled: primitiveColors.gray[100],
    buttonColorTertiary: white,
    buttonBorderTertiary: '1px solid #00000029',

    // Card
    cardBorder: 'none',
    cardBorderActive: 'none',
    cardBorderPrint: `1px solid ${primitiveColors.gray[300]}`,
    cardBackground: white,
    cardBorderRadius: `${primitiveSizes.radius.standard}px`,
    cardPadding: base.spacingRaw(4),
    cardBackgroundActive: 'linear-gradient(136.45deg, #4361EE 0%, #52BDD5 100%)',
    cardTextActive: '#fff',
    cardSupportingTextColor: primitiveColors.gray[750],

    // Selectable Grid Item
    selectableBorderColor: primitiveColors.gray[300],
    selectableBorderColorFocus: primitiveColors.gray[300],
    selectableBorderColorActive: colorPrimary,

    // accordion
    accordionBorder: `1px solid ${primitiveColors.gray[300]}`,
    accordionBorderActive: `2px solid ${colorPrimary}`,
    accordionBorderDisabled: `2px solid ${primitiveColors.gray[300]}`,
    accordionTitleColor: black,
    accordionTitleColorDisabled: primitiveColors.gray[500],

    // Input
    inputBackground: primitiveColors.gray[200],
    inputActiveBackground: primitiveColors.gray[300],
    inputReadOnlyBackground: '#fff',
    inputReadOnlyActiveBackground: '#fff',
    inputBorder: '2px solid transparent',
    inputBorderActive: '2px solid transparent',
    inputBorderReadOnly: `1px solid ${primitiveColors.gray[300]}`,
    inputBorderReadOnlyActive: `2px solid ${primitiveColors.gray[300]}`,
    inputBorderColor: colorSecondary,
    inputActiveColor: orange,
    inputLabelColor: primitiveColors.gray[800],
    inputInputColor: black,
    inputMessageBorder: '2px solid',
    inputMessageColor: white,
    inputMessageColorInfo: brandColor,
    inputMessageColorError: primitiveColors.red[500],
    inputReadOnlyIconColor: primitiveColors.gray[800],

    listHeaderColor: primitiveColors.gray[750],
    tableHeaderColor: primitiveColors.gray[800],

    // Simple Table
    simpleTableBorderColor: primitiveColors.gray[200],
    simpleTableHeaderColor: black,
    simpleTableBodyColor: primitiveColors.gray[800],

    actionLinkTextColor: brandColor,

    checkedAvatarColor: primitiveColors.green[500],
    avatarSecondaryColor: primitiveColors.gray[700],

    // Menu
    menuBackgroundColor: white,
    menuDividerColor: primitiveColors.gray[100],

    // Side menu
    sideMenuIconColor: black,
    sideMenuAlternativeBackground: primitiveColors.gray[100],

    // Group
    groupDivider: primitiveColors.gray[200],

    // Chip
    chipColor: primitiveColors.gray[200],

    // Progressindicator
    progressDonutRingColor: primitiveColors.gray[300],
    progressDonutIndicatorColor1: colorPrimary,
    progressDonutIndicatorColor2: '#52BDD4',
    progressSpinnerColor: primitiveColors.gray[800],

    // RadioList
    radioListBackgroundColors: [white, beige],
    radioListTextColors: [black, black, white],

    // Tooltip
    tooltipBackground: black,
    tooltipBorder: 'none',
    tooltipBorderRadius: `${primitiveSizes.radius.standard}px`,
    tooltipColor: white,
    tooltipShadow: '0px 2px 10px 0px #00000012',

    // Toast
    toastBackground: primitiveColors.gray[800],
    toastBorder: `1px solid ${primitiveColors.gray[200]}`,
    toastBorderRadius: `${primitiveSizes.radius.standard}px`,
    toastTitleColor: white,
    toastColor: white,
    toastShadow: '0px 2px 10px 0px #00000012',
};
